import { useState, useEffect, useRef, useCallback } from 'react';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Checkbox } from '../components/ui/checkbox';
import {
  Upload, Loader2, CheckCircle, XCircle, AlertTriangle, Download,
  ArrowLeft, RefreshCw, Zap, Shield, Users, ArrowRight, FileCheck, ClipboardList,
} from 'lucide-react';
import { toast } from 'sonner';

function TypoFixTab({ jobId, onStatsChange }) {
  const [typos, setTypos] = useState([]);
  const [total, setTotal] = useState(0);
  const [approvedCount, setApprovedCount] = useState(0);
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState(new Set());
  const [approving, setApproving] = useState(false);
  const [approvingAll, setApprovingAll] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/email-cleaner/typo-list/${jobId}`, { params: { page, limit: 50 } });
      setTypos(r.data.items);
      setTotal(r.data.total);
      setApprovedCount(r.data.approved_count);
    } catch (e) { console.error(e); }
  }, [jobId, page]);

  useEffect(() => { load(); }, [load]);

  const toggleSelect = (email) => {
    setSelected(prev => {
      const n = new Set(prev);
      n.has(email) ? n.delete(email) : n.add(email);
      return n;
    });
  };

  const toggleAll = () => {
    if (selected.size === typos.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(typos.map(t => t.normalized)));
    }
  };

  const handleApprove = async () => {
    if (selected.size === 0) return toast.error('Email seciniz');
    setApproving(true);
    try {
      const r = await api.post(`/email-cleaner/typo-approve/${jobId}`, { emails: [...selected] });
      toast.success(`${r.data.approved} email duzeltildi, ${r.data.rejected_suppression + r.data.rejected_duplicate + r.data.rejected_invalid} reddedildi`);
      setSelected(new Set());
      load();
      onStatsChange();
    } catch (e) { toast.error('Onay hatasi'); }
    setApproving(false);
  };

  const handleApproveAll = async () => {
    setApprovingAll(true);
    try {
      const r = await api.post(`/email-cleaner/typo-approve-all/${jobId}`);
      toast.success(`Toplu onay: ${r.data.approved} duzeltildi, ${r.data.rejected_suppression} suppression, ${r.data.rejected_duplicate} duplicate`);
      load();
      onStatsChange();
    } catch (e) { toast.error('Toplu onay hatasi'); }
    setApprovingAll(false);
  };

  if (total === 0 && approvedCount === 0) {
    return <div data-testid="typo-empty" className="text-center py-12 text-muted-foreground">Bu is icin duzeltilecek typo email yok</div>;
  }

  return (
    <div className="space-y-4">
      {/* Summary bar */}
      <div data-testid="typo-summary" className="flex items-center justify-between rounded-lg bg-card p-4">
        <div className="flex gap-6 text-sm">
          <span>Bekleyen: <strong className="text-purple-400">{total}</strong></span>
          <span>Onaylanan: <strong className="text-green-500">{approvedCount}</strong></span>
        </div>
        <div className="flex gap-2">
          {total > 0 && (
            <>
              <Button data-testid="typo-approve-selected" size="sm" onClick={handleApprove} disabled={approving || selected.size === 0}>
                {approving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <CheckCircle className="w-4 h-4 mr-1" />}
                Secilenleri Onayla ({selected.size})
              </Button>
              <Button data-testid="typo-approve-all" size="sm" variant="outline" onClick={handleApproveAll} disabled={approvingAll}>
                {approvingAll ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Zap className="w-4 h-4 mr-1" />}
                Tumunu Onayla ({total})
              </Button>
            </>
          )}
          <Button variant="outline" size="sm" onClick={() => {
            window.open(`${api.defaults.baseURL}/email-cleaner/export/${jobId}/typo_approved?token=${localStorage.getItem('token')}`, '_blank');
          }}>
            <Download className="w-4 h-4 mr-1" /> Onaylanan CSV
          </Button>
          <Button variant="outline" size="sm" onClick={() => {
            window.open(`${api.defaults.baseURL}/email-cleaner/export/${jobId}/typo_rejected?token=${localStorage.getItem('token')}`, '_blank');
          }}>
            <Download className="w-4 h-4 mr-1" /> Bekleyen CSV
          </Button>
        </div>
      </div>

      {/* Typo table */}
      {total > 0 && (
        <div className="overflow-x-auto rounded-lg border border-border">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b bg-muted/30 text-xs text-muted-foreground">
                <th className="py-2 px-3 w-10">
                  <Checkbox data-testid="typo-select-all" checked={selected.size === typos.length && typos.length > 0} onCheckedChange={toggleAll} />
                </th>
                <th className="text-left py-2 px-2">Orijinal Email</th>
                <th className="px-2 text-center w-10"><ArrowRight className="w-4 h-4 mx-auto" /></th>
                <th className="text-left py-2 px-2">Duzeltilmis Email</th>
                <th className="px-2">Hata Domain</th>
                <th className="px-2">Risk</th>
              </tr>
            </thead>
            <tbody>
              {typos.map((t, i) => (
                <tr key={i} className={`border-b border-border/50 hover:bg-muted/20 ${selected.has(t.normalized) ? 'bg-primary/5' : ''}`}>
                  <td className="py-1.5 px-3">
                    <Checkbox checked={selected.has(t.normalized)} onCheckedChange={() => toggleSelect(t.normalized)} />
                  </td>
                  <td className="py-1.5 px-2 font-mono text-xs text-red-400 line-through">{t.normalized}</td>
                  <td className="px-2 text-center"><ArrowRight className="w-3 h-3 mx-auto text-muted-foreground" /></td>
                  <td className="py-1.5 px-2 font-mono text-xs text-green-400 font-medium">{t.suggested}</td>
                  <td className="px-2 text-xs text-muted-foreground">{t.reason?.replace('yazim_hatasi_', '')}</td>
                  <td className="px-2 text-xs">{t.risk_score}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {total > 50 && (
        <div className="flex justify-center gap-2 mt-3">
          <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)}>Onceki</Button>
          <span className="text-xs text-muted-foreground py-2">Sayfa {page}/{Math.ceil(total / 50)}</span>
          <Button variant="outline" size="sm" disabled={page >= Math.ceil(total / 50)} onClick={() => setPage(page + 1)}>Sonraki</Button>
        </div>
      )}
    </div>
  );
}

function AuditLogTab({ jobId }) {
  const [audits, setAudits] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get(`/email-cleaner/typo-audit/${jobId}`, { params: { page, limit: 50 } });
        setAudits(r.data.items);
        setTotal(r.data.total);
      } catch (e) { console.error(e); }
    })();
  }, [jobId, page]);

  if (total === 0) return <div className="text-center py-12 text-muted-foreground">Henuz duzeltme islemi yapilmadi</div>;

  return (
    <div className="space-y-4">
      <div className="overflow-x-auto rounded-lg border border-border">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b bg-muted/30 text-xs text-muted-foreground">
              <th className="text-left py-2 px-2">Orijinal</th>
              <th className="text-left px-2">Duzeltilmis</th>
              <th className="px-2">Islem</th>
              <th className="px-2">Sebep</th>
              <th className="px-2">Yeni Risk</th>
              <th className="px-2">Onaylayan</th>
              <th className="px-2">Tarih</th>
            </tr>
          </thead>
          <tbody>
            {audits.map((a, i) => (
              <tr key={i} className="border-b border-border/50 hover:bg-muted/20">
                <td className="py-1.5 px-2 font-mono text-xs">{a.original_email}</td>
                <td className="py-1.5 px-2 font-mono text-xs">{a.suggested_email}</td>
                <td className="px-2">
                  <Badge variant={a.action === 'approved' ? 'default' : 'destructive'} className="text-[10px]">{a.action}</Badge>
                </td>
                <td className="px-2 text-xs text-muted-foreground">{a.reject_reason || '-'}</td>
                <td className="px-2 text-xs">{a.new_risk_score}</td>
                <td className="px-2 text-xs text-muted-foreground">{a.approved_by}</td>
                <td className="px-2 text-xs text-muted-foreground">{a.approved_at?.slice(0, 16)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {total > 50 && (
        <div className="flex justify-center gap-2">
          <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)}>Onceki</Button>
          <span className="text-xs text-muted-foreground py-2">Sayfa {page}/{Math.ceil(total / 50)}</span>
          <Button variant="outline" size="sm" disabled={page >= Math.ceil(total / 50)} onClick={() => setPage(page + 1)}>Sonraki</Button>
        </div>
      )}
    </div>
  );
}

function JobDetail({ jobId, onBack }) {
  const [job, setJob] = useState(null);
  const [results, setResults] = useState([]);
  const [filter, setFilter] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [activeTab, setActiveTab] = useState('overview');
  const pollingRef = useRef(null);

  const loadJob = async () => {
    const r = await api.get(`/email-cleaner/jobs/${jobId}`);
    setJob(r.data);
    if (['completed', 'failed'].includes(r.data.status) && pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
  };

  const loadResults = async () => {
    const params = { page, limit: 50 };
    if (filter) params.status = filter;
    const r = await api.get(`/email-cleaner/results/${jobId}`, { params });
    setResults(r.data.items);
    setTotal(r.data.total);
  };

  useEffect(() => {
    loadJob();
    pollingRef.current = setInterval(loadJob, 3000);
    return () => { if (pollingRef.current) clearInterval(pollingRef.current); };
  }, [jobId]);

  useEffect(() => { if (job?.status === 'completed' && activeTab === 'overview') loadResults(); }, [job?.status, filter, page, activeTab]);

  const handleExport = (type) => {
    window.open(`${api.defaults.baseURL}/email-cleaner/export/${jobId}/${type}?token=${localStorage.getItem('token')}`, '_blank');
  };

  const handleToCampaign = async () => {
    try {
      const r = await api.post(`/email-cleaner/to-campaign/${jobId}`, { name: `Temiz Liste - ${job?.filename || jobId.slice(0, 8)}` });
      toast.success(`${r.data.clean_emails} temiz email kampanyaya aktarildi`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Hata'); }
  };

  if (!job) return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin" /></div>;

  const s = job.stats || {};
  const ps = job.provider_stats || {};
  const isRunning = !['completed', 'failed'].includes(job.status);
  const statusColor = (st) => st === 'clean' ? 'text-green-500' : st === 'duplicate' ? 'text-amber-500' : st === 'suppressed' ? 'text-red-400' : st === 'typo' ? 'text-purple-400' : 'text-red-500';

  const tabs = [
    { id: 'overview', label: 'Genel', icon: Users },
    { id: 'typo-fix', label: 'Typo Duzelt', icon: FileCheck, count: job.typo_count },
    { id: 'audit', label: 'Islem Gecmisi', icon: ClipboardList },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={onBack} data-testid="back-button"><ArrowLeft className="w-5 h-5" /></Button>
          <div>
            <h1 className="text-2xl font-heading font-bold">{job.filename}</h1>
            <Badge variant={job.status === 'completed' ? 'default' : job.status === 'failed' ? 'destructive' : 'secondary'}>{job.status}</Badge>
          </div>
        </div>
        {job.status === 'completed' && (
          <div className="flex gap-2">
            <Button data-testid="to-campaign-btn" size="sm" onClick={handleToCampaign}><Zap className="w-4 h-4 mr-1" /> Kampanyaya Aktar</Button>
            <Button variant="outline" size="sm" onClick={() => handleExport('clean')}><Download className="w-4 h-4 mr-1" /> Temiz CSV</Button>
            <Button variant="outline" size="sm" onClick={() => handleExport('rejected')}><Download className="w-4 h-4 mr-1" /> Red CSV</Button>
            <Button variant="outline" size="sm" onClick={() => handleExport('full')}><Download className="w-4 h-4 mr-1" /> Tam Rapor</Button>
          </div>
        )}
      </div>

      {isRunning && <div className="rounded-lg bg-card p-4"><Progress value={job.progress || 0} className="h-3" /><p className="text-xs text-muted-foreground mt-2 text-right">{job.status} — %{job.progress}</p></div>}

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 lg:grid-cols-10 gap-3">
        {[
          { label: 'Toplam', value: job.extracted_count || job.original_count, icon: Users },
          { label: 'Temiz', value: job.clean_count, icon: CheckCircle, color: 'text-green-500' },
          { label: 'Duplicate', value: job.duplicate_count, icon: Users, color: 'text-amber-500' },
          { label: 'Suppressed', value: job.suppressed_count, icon: Shield, color: 'text-red-400' },
          { label: 'Invalid', value: job.invalid_count, icon: XCircle, color: 'text-red-500' },
          { label: 'Typo', value: job.typo_count, icon: AlertTriangle, color: 'text-purple-400' },
          { label: 'Fake/Riskli', value: job.fake_count, icon: AlertTriangle, color: 'text-orange-500' },
          { label: 'Disposable', value: job.blocked_domain_count, icon: Shield, color: 'text-rose-500' },
          { label: 'Hard Bounce', value: s.hard_bounce || 0, icon: XCircle, color: 'text-red-600' },
          { label: 'Temiz Oran', value: job.extracted_count ? `%${((job.clean_count || 0) / (job.extracted_count || 1) * 100).toFixed(1)}` : '%0', icon: Zap, color: 'text-emerald-400', isText: true },
        ].map((stat, i) => (
          <div key={i} className="rounded-lg bg-card p-3 text-center" data-testid={`stat-${stat.label.toLowerCase().replace(/[\s/]/g, '-')}`}>
            <stat.icon className={`w-4 h-4 mx-auto mb-1 ${stat.color || 'text-muted-foreground'}`} />
            <p className="text-lg font-bold">{stat.isText ? stat.value : (stat.value || 0).toLocaleString()}</p>
            <p className="text-[10px] text-muted-foreground">{stat.label}</p>
          </div>
        ))}
      </div>

      {/* Tabs */}
      {job.status === 'completed' && (
        <>
          <div className="flex gap-1 border-b border-border">
            {tabs.map(tab => (
              <button
                key={tab.id}
                data-testid={`tab-${tab.id}`}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === tab.id
                    ? 'border-primary text-primary'
                    : 'border-transparent text-muted-foreground hover:text-foreground'
                }`}
              >
                <tab.icon className="w-4 h-4" />
                {tab.label}
                {tab.count > 0 && (
                  <Badge variant="secondary" className="text-[10px] px-1.5 py-0">{tab.count}</Badge>
                )}
              </button>
            ))}
          </div>

          {/* Tab Content */}
          {activeTab === 'overview' && (
            <div className="space-y-4">
              {/* Provider breakdown */}
              {Object.keys(ps).length > 0 && (
                <Card><CardHeader className="pb-3"><CardTitle className="text-base">Provider Dagilimi</CardTitle></CardHeader><CardContent>
                  <div className="flex gap-3 flex-wrap">{Object.entries(ps).sort((a, b) => b[1] - a[1]).map(([p, c]) => (
                    <div key={p} className="px-3 py-2 rounded bg-muted/30 text-center"><p className="text-lg font-bold">{c.toLocaleString()}</p><p className="text-[10px] text-muted-foreground">{p}</p></div>
                  ))}</div>
                </CardContent></Card>
              )}

              {/* Results table */}
              <Card><CardHeader className="pb-3"><div className="flex items-center justify-between"><CardTitle className="text-base">Detay ({total.toLocaleString()})</CardTitle>
                <div className="flex gap-1">{['', 'clean', 'suppressed', 'duplicate', 'invalid', 'typo', 'fake', 'blocked_domain'].map(f => (
                  <button key={f} onClick={() => { setFilter(f); setPage(1); }} className={`px-2 py-0.5 rounded text-[10px] ${filter === f ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'}`}>{f || 'Tumu'}</button>
                ))}</div>
              </div></CardHeader><CardContent>
                <div className="overflow-x-auto"><table className="w-full text-sm"><thead><tr className="border-b text-xs text-muted-foreground">
                  <th className="text-left py-2 px-2">Email</th><th className="px-2">Status</th><th className="px-2">Sebep</th><th className="px-2">Risk</th><th className="px-2">Provider</th><th className="px-2">Oneri</th>
                </tr></thead><tbody>{results.map((r, i) => (
                  <tr key={i} className="border-b border-border/50 hover:bg-muted/20">
                    <td className="py-1.5 px-2 font-mono text-xs">{r.normalized}</td>
                    <td className={`px-2 text-xs font-medium ${statusColor(r.status)}`}>{r.status}</td>
                    <td className="px-2 text-xs text-muted-foreground">{r.reason?.slice(0, 30)}</td>
                    <td className="px-2 text-xs">{r.risk_score}</td>
                    <td className="px-2 text-xs">{r.provider}</td>
                    <td className="px-2 text-xs text-blue-400">{r.suggested}</td>
                  </tr>
                ))}</tbody></table></div>
                {total > 50 && <div className="flex justify-center gap-2 mt-3">
                  <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)}>Onceki</Button>
                  <span className="text-xs text-muted-foreground py-2">Sayfa {page}/{Math.ceil(total / 50)}</span>
                  <Button variant="outline" size="sm" disabled={page >= Math.ceil(total / 50)} onClick={() => setPage(page + 1)}>Sonraki</Button>
                </div>}
              </CardContent></Card>
            </div>
          )}

          {activeTab === 'typo-fix' && <TypoFixTab jobId={jobId} onStatsChange={loadJob} />}
          {activeTab === 'audit' && <AuditLogTab jobId={jobId} />}
        </>
      )}
    </div>
  );
}

export default function EmailCleanerPage() {
  const [view, setView] = useState('list');
  const [selectedJob, setSelectedJob] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);

  const load = async () => { try { const r = await api.get('/email-cleaner/jobs'); setJobs(r.data); } catch (e) { } setLoading(false); };
  useEffect(() => { load(); }, []);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await api.post('/email-cleaner/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success(`${r.data.lines.toLocaleString()} satir yuklendi, temizleme baslatildi`);
      setSelectedJob(r.data.job_id);
      setView('detail');
    } catch (e) { toast.error('Upload hatasi'); }
    setUploading(false);
    e.target.value = '';
  };

  if (view === 'detail' && selectedJob) return <JobDetail jobId={selectedJob} onBack={() => { setSelectedJob(null); setView('list'); load(); }} />;

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin" /></div>;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div><h1 className="text-2xl font-heading font-bold">Email Temizleme</h1><p className="text-muted-foreground mt-1">Email listelerini temizle, dogrula ve kampanyaya aktar</p></div>
        <Button variant="outline" size="sm" onClick={() => { setLoading(true); load(); }}><RefreshCw className="w-4 h-4 mr-1" /> Yenile</Button>
      </div>

      {/* Upload */}
      <Card><CardContent className="py-8"><label className="flex flex-col items-center justify-center cursor-pointer" data-testid="upload-area">
        <Upload className="w-10 h-10 text-muted-foreground mb-3" />
        <p className="text-lg font-medium">{uploading ? 'Yukleniyor...' : 'Email Listesi Yukle'}</p>
        <p className="text-sm text-muted-foreground mt-1">CSV, TXT veya XLSX — 1.5M+ email desteklenir</p>
        <input type="file" accept=".csv,.txt,.xlsx" className="hidden" onChange={handleUpload} disabled={uploading} data-testid="file-input" />
        {uploading && <Loader2 className="w-6 h-6 mt-3 animate-spin" />}
      </label></CardContent></Card>

      {/* Jobs */}
      {jobs.length > 0 && <div className="space-y-3">{jobs.map(j => (
        <Card key={j.id} className="cursor-pointer hover:border-primary/40" onClick={() => { setSelectedJob(j.id); setView('detail'); }} data-testid={`job-card-${j.id}`}>
          <CardContent className="flex items-center justify-between py-4">
            <div>
              <p className="font-medium">{j.filename}</p>
              <div className="flex gap-3 mt-1 text-xs text-muted-foreground">
                <span>{(j.original_count || 0).toLocaleString()} satir</span>
                {j.clean_count > 0 && <span className="text-green-500">{j.clean_count.toLocaleString()} temiz</span>}
                {j.rejected_count > 0 && <span className="text-red-400">{j.rejected_count.toLocaleString()} red</span>}
              </div>
            </div>
            <div className="flex items-center gap-2">
              {j.status !== 'completed' && j.status !== 'failed' && <Loader2 className="w-4 h-4 animate-spin" />}
              <Badge variant={j.status === 'completed' ? 'default' : j.status === 'failed' ? 'destructive' : 'secondary'}>{j.status}</Badge>
            </div>
          </CardContent>
        </Card>
      ))}</div>}
    </div>
  );
}
